import _ from 'lodash';
import { Autocomplete, AutocompleteProps, AutocompleteValue, createFilterOptions } from '@mui/material';
import {
  Controller,
  ControllerProps,
  Path,
  Control,
  PathValue,
  UnpackNestedValue,
  FieldValues as TFieldValues,
} from 'react-hook-form';
import { AutocompleteOption } from '../autocomplete-option/AutocompleteOption';
import { TextInput } from '../text-input/TextInput';
import { AutocompleteSelectValue, MaybeBoolean, SelectOption } from './types';
import { StrOrNum } from '../../types/util';

export interface ControlledAutocompleteInputProps<
  FieldValues extends TFieldValues,
  Multiple extends MaybeBoolean,
  DisableClearable extends MaybeBoolean,
  FreeSolo extends MaybeBoolean
> extends Omit<ControllerProps, 'render' | 'name' | 'control'> {
  id?: string;
  name: Path<FieldValues>;
  error?: string;
  AutocompleteProps?: Partial<AutocompleteProps<SelectOption, Multiple, DisableClearable, FreeSolo>>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  control: Control<FieldValues>;
  isError?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  loadingMessage?: string;
  onChangeCallback?: (value: AutocompleteSelectValue) => void;
  onNewOptionCreated?: (value: StrOrNum) => void;
}

const _filterOptions = createFilterOptions<SelectOption>({
  limit: 100,
});
export const ControlledAutocompleteInput = <
  FieldValues extends TFieldValues,
  Multiple extends MaybeBoolean = undefined,
  DisableClearable extends MaybeBoolean = undefined,
  FreeSolo extends MaybeBoolean = undefined
>({
  control,
  name,
  id,
  error,
  AutocompleteProps,
  label,
  placeholder,
  required,
  isError,
  isLoading,
  loadingMessage = 'Loading...',
  errorMessage = 'Could not connect to server',
  onChangeCallback,
  onNewOptionCreated,
}: ControlledAutocompleteInputProps<FieldValues, Multiple, DisableClearable, FreeSolo>) => {
  type Value = AutocompleteValue<SelectOption, Multiple, DisableClearable, FreeSolo>;

  const formatValue = (mayBeOptionArr: Value | null): AutocompleteSelectValue => {
    if (!mayBeOptionArr) {
      if (AutocompleteProps?.multiple) return [];
      return '';
    }

    if (Array.isArray(mayBeOptionArr)) {
      return mayBeOptionArr.map((option) => {
        if (typeof option === 'object') return option.value;
        return option;
      });
    }
    if (typeof mayBeOptionArr === 'object') return mayBeOptionArr.value;
    return mayBeOptionArr;
  };

  const getValue = (value: UnpackNestedValue<PathValue<FieldValues, Path<FieldValues>>>): Value => {
    if (isLoading || !AutocompleteProps?.options || AutocompleteProps.options.length === 0) {
      if (AutocompleteProps?.multiple) return [] as unknown as Value;
      return null as Value;
    }

    if (!value) {
      const defaultValue = AutocompleteProps.multiple ? [] : null;
      return (AutocompleteProps.defaultValue || defaultValue) as Value;
    }

    if (AutocompleteProps.multiple) {
      const values = _.filter(AutocompleteProps.options, (option) => {
        if (Array.isArray(value)) {
          return _.findIndex(value, (valueItem) => _.isEqual(valueItem, option.value)) !== -1;
        }
        return false;
      });
      return (values || []) as unknown as Value;
    } else {
      const currentValue = _.find(AutocompleteProps.options, (option) => _.isEqual(value, option.value));
      return (currentValue || null) as Value;
    }
  };

  const handleChange = (value: Value, changeHandler: (value: ReturnType<typeof formatValue>) => void) => {
    const newValue = formatValue(value);
    if (AutocompleteProps?.freeSolo && onNewOptionCreated && value) {
      const option = _.find(AutocompleteProps.options, (option) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return _.isEqual(value, option.value);
        } else if (!Array.isArray(value) && typeof value === 'object') {
          return _.isEqual(value.value, option.value);
        }
        return true;
      });
      if (!option) {
        if (typeof value === 'string' || typeof value === 'number') {
          onNewOptionCreated(value);
        } else if (!Array.isArray(value) && typeof value === 'object') {
          onNewOptionCreated(value.value);
        }
        return;
      }
    }
    changeHandler(newValue);
    if (onChangeCallback) {
      onChangeCallback(newValue);
    }
  };

  const filterOptions: typeof _filterOptions = (options, state): SelectOption[] => {
    const filtered = _filterOptions(options, state);
    if (AutocompleteProps?.freeSolo) {
      if (state.inputValue.trim().length > 0) {
        filtered.push({
          label: `Add "${state.inputValue}"`,
          value: state.inputValue,
        });
      }
    }
    return filtered;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, name: fieldName, onBlur, ref, value } }) => (
        <Autocomplete<SelectOption, Multiple, DisableClearable, FreeSolo>
          filterOptions={filterOptions}
          {...AutocompleteProps}
          value={getValue(value)}
          id={id}
          options={AutocompleteProps?.options || []}
          onChange={(_e, value) => handleChange(value, onChange)}
          isOptionEqualToValue={(option, value) => _.isEqual(option, value)}
          loading={isLoading}
          loadingText={loadingMessage}
          noOptionsText={isError ? errorMessage : 'No Options Found'}
          disableCloseOnSelect={AutocompleteProps?.multiple}
          blurOnSelect={!AutocompleteProps?.multiple}
          renderInput={(params) => {
            return (
              <TextInput
                FieldProps={{
                  ...params,
                  name: fieldName,
                  onBlur,
                  inputRef: ref,
                }}
                label={label}
                required={required}
                placeholder={placeholder}
                error={error}
              />
            );
          }}
          renderOption={(props, option) => {
            return (
              <AutocompleteOption {...props} label={option.label} helperText={option.helperText} key={option.value} />
            );
          }}
        />
      )}
    />
  );
};
